import { useTranslation } from "@privacy/translations";
import type { FC } from "react";
import type { GridColDef } from "@mui/x-data-grid-premium";
import { useForm } from "react-hook-form";
import { usePrivacyDocumentTitle } from "../../shared/usePrivacyDocumentTitle";
import { DynamicGrid } from "../../dynamicGrid";
import { MeasureIcon2, DpiaIcon3, RiskIcon3 } from "../../shared/icons";
import { usePrivacyNavigate } from "../../shared/usePrivacyNavigate";
import { requiredColDef } from "../../dynamicGrid/misc";
import { EmptyState } from "../../shared/EmptyState";
import { PageTitle } from "../../shared/Page";
import * as FormDialog from "../../shared/FormDialog";
import type { GridViewMeasure } from "../../api/measures.types";
import {
  createMeasureMutationOptions,
  measureFormQueryOptions,
  measuresGridViewQueryOptions,
} from "../../api/measures";
import { useGetUsersFromCurrentOrganisation } from "../../api/users";
import { usePermission } from "../../shared/permissions";
import { EmptyCell } from "../../dynamicGrid/columnDefinitions";
import { Grid } from "../../shared/Grid";
import { MeasuresInlineDrawer } from "./MeasuresDrawer/MeasuresDrawer";
import { ConnectedRisksSubDrawer } from "./MeasuresDrawer/ConnectedRisksSubDrawer";
import { ConnectedDpiasSubDrawer } from "./MeasuresDrawer/ConnectedDpiasSubDrawer";
import { MeasureActionMenu } from "./MeasuresDrawer/MeasuresDrawerHeader";
import { useMutation, useQuery } from "@tanstack/react-query";
import { prefetch } from "../../api/prefetch";

const MeasuresGridPage = () => {
  usePrivacyDocumentTitle("Sidebar.Measures");
  const { t } = useTranslation();
  const { navigate, paths } = usePrivacyNavigate();
  const measureGridView = useQuery(measuresGridViewQueryOptions());
  const measureForm = useQuery(measureFormQueryOptions());
  const usersQueryResult = useGetUsersFromCurrentOrganisation();

  return (
    <DynamicGrid.Grid
      rowsData={measureGridView.data?.measures}
      form={measureForm.data}
      getRowId={getRowId}
      asyncQuestionCardData={{ usersQueryData: usersQueryResult.data }}
      mainColumnProps={{
        headerName: t("Measures.Grid.NameHeader"),
        renderLabel: ({ name }) => name,
        renderIcon: ({ iconProps }) => <MeasureIcon2 {...iconProps} />,
      }}
      getColumns={questionCardColumns => [
        ...questionCardColumns,
        connectedDpiasColumn({ headerName: t("Measures.Grid.DpiaHeader") }),
        connectedRisksColumn({ headerName: t("Measures.Grid.RisksHeader") }),
      ]}
      Toolbar={MeasuresGridToolbar}
      Footer={MeasuresGridFooter}
      NoRowsOverlay={NoMeasuresOverlay}
      texts={{ searchFieldPlaceholder: t("Measures.Grid.SearchFieldLabel") }}
      onRowSelect={selected => navigate(paths.measuresInline(selected))}
      renderActionMenu={({ row, selectedRowId }) => (
        <MeasureActionMenu
          measureId={row.id}
          measureName={row.name}
          onDeleted={() => {
            if (selectedRowId === row.id) navigate(paths.measuresGrid());
          }}
        />
      )}
    />
  );
};

const getRowId = (row: GridViewMeasure) => row.id;

const connectedDpiasColumn = ({ headerName }: { headerName: string }): GridColDef<GridViewMeasure> => ({
  ...requiredColDef({ headerName, field: "connectedDpias" }),
  width: 280,
  valueGetter: (_, row) => {
    return row.connectedDpias.entity.map(dpia => dpia.name).join(Grid.VALUE_GETTER_DELIMITER);
  },
  renderCell: ({ row }) => {
    if (!row) return <EmptyCell />;
    return (
      <DynamicGrid.Cell>
        <ConnectedDpiaCell id={row.id} count={row.connectedDpias.totalCount} />
      </DynamicGrid.Cell>
    );
  },
});

const ConnectedDpiaCell: FC<{ id: string; count: number }> = ({ id, count }) => {
  const { paths } = usePrivacyNavigate();
  return <DynamicGrid.OpenSubDrawerLink to={paths.measuresConnectedDpias(id)} label={count} Icon={DpiaIcon3} />;
};

const connectedRisksColumn = ({ headerName }: { headerName: string }): GridColDef<GridViewMeasure> => ({
  ...requiredColDef({ headerName, field: "connectedRisks" }),
  width: 280,
  valueGetter: (_, row) => {
    return row.connectedRisks.entity.map(risk => risk.name).join(Grid.VALUE_GETTER_DELIMITER);
  },
  renderCell: ({ row }) => {
    if (!row) return <EmptyCell />;
    return (
      <DynamicGrid.Cell>
        <ConnectedRisksCell id={row.id} count={row.connectedRisks.totalCount} />
      </DynamicGrid.Cell>
    );
  },
});

const ConnectedRisksCell: FC<{ id: string; count: number }> = ({ id, count }) => {
  const { paths } = usePrivacyNavigate();
  return <DynamicGrid.OpenSubDrawerLink to={paths.measuresConnectedRisks(id)} label={count} Icon={RiskIcon3} />;
};

const MeasuresGridFooter = () => <DynamicGrid.Footer numberOfRowsTextKey="Measures.Grid.NumberOfRows" />;

const NoMeasuresOverlay = () => {
  return <EmptyState labelKey="Measures.Grid.NoRows" />;
};

const MeasuresGridToolbar: FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const { navigate, paths } = usePrivacyNavigate();

  return (
    <DynamicGrid.Toolbar
      pageTitle={<PageTitle title={t("Measures.Title")} subTitle={t("Measures.SubTitle")} />}
      actionButtons={
        <>
          <DynamicGrid.ExportButton />
          {hasPermission("nova-forms-api:measure:create") && (
            <DynamicGrid.CreateNewButton
              labelKey="CreateMeasure.Button"
              renderDialog={({ onClose }) => (
                <CreateMeasureDialog
                  onClose={onClose}
                  onCreated={measure => navigate(paths.measuresInline(measure.id))}
                />
              )}
            />
          )}
        </>
      }
    />
  );
};

type CreateMeasureDialogProps = {
  onClose: () => void;
  onCreated: (measure: { id: string }) => void;
  defaultName?: string;
};

const CreateMeasureDialog: FC<CreateMeasureDialogProps> = ({ onClose, onCreated, defaultName = "" }) => {
  const { t } = useTranslation();
  const createMeasureMutation = useMutation(createMeasureMutationOptions());
  const { handleSubmit, reset, control, setFocus } = useForm<{ name: string }>({
    defaultValues: { name: defaultName },
  });

  FormDialog.useAutoFocusField(setFocus, "name");

  const handleNewMeasure = ({ name }: { name: string }) => {
    return createMeasureMutation.mutate(
      { name },
      {
        onSuccess: async ({ id }) => {
          onClose();
          reset();
          onCreated({ id });
        },
      }
    );
  };

  const handleCancel = () => {
    onClose();
    reset();
  };

  return (
    <FormDialog.Dialog isOpen={true}>
      <FormDialog.Title>{t("CreateMeasure.Dialog.Title")}</FormDialog.Title>

      <FormDialog.Form onSubmit={handleSubmit(handleNewMeasure)}>
        <FormDialog.Content>
          <FormDialog.FormError isError={createMeasureMutation.isError} />
          <FormDialog.Fields>
            <FormDialog.TextField
              control={control}
              name="name"
              rules={{ required: t("CreateMeasure.Dialog.Validation.NameRequired") }}
              label={t("CreateMeasure.Dialog.NameLabel")}
              placeholder={t("CreateMeasure.Dialog.NamePlaceholder")}
            />
          </FormDialog.Fields>
        </FormDialog.Content>

        <FormDialog.ActionButtons>
          <FormDialog.CancelButton onClick={handleCancel} />
          <FormDialog.SubmitButton isSubmitting={createMeasureMutation.isPending}>
            {t("CreateMeasure.Dialog.SubmitButton")}
          </FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};

export const measuresGridPageRoutes = DynamicGrid.setupRoutes({
  gridElement: <MeasuresGridPage />,
  rowDrawerRoutes: [
    { index: true, element: <MeasuresInlineDrawer /> },
    { path: "dpias", element: <ConnectedDpiasSubDrawer /> },
    { path: "risks", element: <ConnectedRisksSubDrawer /> },
  ],
});

export const prefetchMeasuresGridViewAndForm = () => {
  prefetch(measuresGridViewQueryOptions());
  prefetch(measureFormQueryOptions());
};
